// Single source of truth for /tools/assistance-funding-availability.

const SITE = "https://www.homeapproach.com";

export const PAGE_PATH = "/tools/assistance-funding-availability";
export const PAGE_URL = `${SITE}${PAGE_PATH}`;
export const PAGE_TITLE =
  "Why Assistance Programs Run Out of Funding Mid-Year and How to Check Availability";
export const PAGE_DESCRIPTION =
  "Assistance funds close mid-year for predictable reasons. Learn the funding cycle, check availability in the right order, read status words correctly, and know what to do when a program is paused or closed.";

export const HERO_SUMMARY =
  "Most housing, utility, and rental assistance runs on a fixed pot of money released once a year and paid out first-come, first-served. When the pot empties, applications stop — even though the program still exists.";

export const HERO_ACTIONS = [
  "Right now: call 211 and ask which local funds are still open today, not which programs exist.",
  "Then: check your state agency's status page and get your documents together so you can apply the hour a program reopens.",
];

export const SECTIONS = [
  { id: "cycle", title: "Why funding runs out mid-year" },
  { id: "check-order", title: "Check availability in this order" },
  { id: "state", title: "Where to check your state program directly" },
  { id: "reopening", title: "Reopening patterns and what status words mean" },
  { id: "documents", title: "Documents to have ready" },
  { id: "fallback", title: "When public funds are closed" },
  { id: "intents", title: "Quick answers" },
  { id: "faq", title: "Common questions" },
];

export interface CycleRow {
  id: string;
  phase: string;
  cause: string;
  detail: string;
}

export const CYCLE_ROWS: CycleRow[] = [
  {
    id: "block-grants",
    phase: "Start of the program year",
    cause: "Block grant money arrives in one lump",
    detail:
      "Programs like LIHEAP, ESG, and many rental funds are financed by federal block grants passed to states, then to local administrators. The allocation is set for the year. There is commonly no mid-year top-up unless Congress or the state legislature adds one.",
  },
  {
    id: "first-come",
    phase: "First weeks",
    cause: "First-come, first-served processing",
    detail:
      "Most administrators approve applications in the order they are completed rather than by need ranking. A large share of the annual pot is frequently committed in the first several weeks the window is open.",
  },
  {
    id: "seasonal",
    phase: "Peak season",
    cause: "Seasonal demand spikes",
    detail:
      "Heating assistance surges in the first cold weeks; cooling assistance surges in the first heat wave; rental assistance spikes after eviction filings rise. Demand arrives in a cluster, not evenly across the year.",
  },
  {
    id: "payout-size",
    phase: "Mid-year",
    cause: "Larger per-household payouts",
    detail:
      "When energy costs or rents rise, agencies often raise the maximum benefit per household. The same total budget then helps fewer families, so the money is exhausted earlier in the calendar.",
  },
  {
    id: "expired-emergency",
    phase: "Late in the cycle",
    cause: "Emergency funds have expired",
    detail:
      "Pandemic-era and disaster supplements that once extended programs past their normal end date have largely wound down in many jurisdictions. Programs that felt well-funded a few years ago may now close months earlier.",
  },
];

export interface CheckPath {
  id: string;
  step: number;
  resourceType: string;
  name: string;
  what: string;
  ask: string;
  statusWords: string[];
  href?: string;
  linkLabel?: string;
}

export const CHECK_PATHS: CheckPath[] = [
  {
    id: "211",
    step: 1,
    resourceType: "211",
    name: "Call or search 211",
    what:
      "A free, statewide referral line and database that tracks which local funds are actually accepting applications this week, including small funds that never appear in search results.",
    ask:
      "\"Which rent, utility, or housing funds in my county are open today?\" Ask for the exact agency name and intake method, not just the program name.",
    statusWords: ["Accepting applications", "Waitlist open", "Intake closed", "Funds exhausted"],
    href: "https://www.211.org",
    linkLabel: "211.org",
  },
  {
    id: "caa",
    step: 2,
    resourceType: "community_action_agency",
    name: "Your Community Action Agency",
    what:
      "CAAs administer LIHEAP and many local emergency funds in most counties. They know their own remaining balance and their reopening date better than any national database does.",
    ask:
      "\"Is the current program year still open, and if not, when does the next intake period begin?\" Ask whether they keep a callback or pre-application list.",
    statusWords: ["Open intake", "Appointment only", "Paused", "Closed for the program year"],
    href: "https://communityactionpartnership.com/find-a-cap/",
    linkLabel: "Find your local CAA",
  },
  {
    id: "rental-db",
    step: 3,
    resourceType: "rental_assistance_database",
    name: "Rental assistance databases",
    what:
      "State and national trackers list emergency rental assistance programs by county with a status field. Useful for finding programs outside your immediate area that still serve your zip code.",
    ask:
      "Filter to your county, then confirm status by calling the listed administrator — database status fields commonly lag the real balance by weeks.",
    statusWords: ["Open", "Limited funds", "Waitlist", "Program ended"],
    href: "https://www.consumerfinance.gov/coronavirus/mortgage-and-housing-assistance/renter-protections/find-help-with-rent-and-utilities/",
    linkLabel: "CFPB rental assistance finder",
  },
  {
    id: "utility",
    step: 4,
    resourceType: "utility_hardship",
    name: "Your utility's hardship team",
    what:
      "Most regulated utilities run their own hardship or fuel funds, separate from LIHEAP, plus arrearage forgiveness and shutoff protections. These are often funded by ratepayers and follow a different calendar than public programs.",
    ask:
      "Ask for the \"customer assistance\" or \"hardship fund\" team specifically, and ask about a payment arrangement or medical hold even if grant money is gone.",
    statusWords: ["Funds available", "Matching grant", "Payment plan only", "Fund depleted"],
  },
  {
    id: "faith",
    step: 5,
    resourceType: "faith_based",
    name: "Faith-based and community aid",
    what:
      "Parish, mosque, temple, and denominational aid networks hold smaller amounts but replenish on their own donation cycles, which means they may have money when public funds do not.",
    ask:
      "Ask whether help is paid directly to the landlord or utility, what the per-household cap is, and whether you need a referral from 211 or a caseworker first.",
    statusWords: ["Accepting requests", "Referral required", "Monthly cap reached", "Next cycle"],
  },
];

export interface StateReferenceRow {
  id: string;
  agency: string;
  programType: string;
  statusLabel: string;
  whatToLookFor: string;
  recheck: string;
  lastVerified: string;
}

export const STATE_REFERENCE_ROWS: StateReferenceRow[] = [
  {
    id: "energy-office",
    agency: "State energy or human services office",
    programType: "LIHEAP, weatherization, crisis heating and cooling",
    statusLabel: "Program year open / closed",
    whatToLookFor:
      "A dated banner on the LIHEAP landing page giving the intake window. If the page shows no date, call — an undated page is not evidence the program is open.",
    recheck: "Weekly during the posted season; monthly otherwise",
    lastVerified: "August 12, 2026",
  },
  {
    id: "housing-agency",
    agency: "State housing finance agency",
    programType: "Rental assistance, homelessness prevention, down payment help",
    statusLabel: "Accepting applications / waitlist / suspended",
    whatToLookFor:
      "A program status table or \"funding availability\" notice. Housing agencies frequently announce reopening dates weeks ahead, so subscribe to their email list.",
    recheck: "Every two weeks, plus at the start of the state fiscal year",
    lastVerified: "August 12, 2026",
  },
  {
    id: "local-admin",
    agency: "Local administrator (county, city, or CAA)",
    programType: "The office that actually takes your application",
    statusLabel: "Intake open / appointment only / closed",
    whatToLookFor:
      "The remaining balance for this county specifically. State-level \"open\" does not mean your county still has money left.",
    recheck: "Weekly if paused; immediately after any state reopening notice",
    lastVerified: "August 12, 2026",
  },
];

export interface StatusTerm {
  id: string;
  term: string;
  meaning: string;
  action: string;
}

export const STATUS_GLOSSARY: StatusTerm[] = [
  {
    id: "open",
    term: "Open / accepting applications",
    meaning: "Money remains and applications are being processed now.",
    action: "Apply today. Completeness beats speed only if you can do both — submit every document at once.",
  },
  {
    id: "waitlist",
    term: "Waitlist",
    meaning:
      "Funds are committed but the agency is queuing households for the next release or for cancellations.",
    action: "Get on it anyway. Waitlist position commonly carries over to the next funding round.",
  },
  {
    id: "paused",
    term: "Paused / temporarily suspended",
    meaning:
      "Intake stopped while the agency processes a backlog or waits on a pending allocation. The program usually returns.",
    action: "Recheck weekly and ask for the expected reopening date in writing or by email.",
  },
  {
    id: "closed",
    term: "Closed for the program year",
    meaning: "The annual allocation is spent. Nothing more will be paid under this cycle.",
    action: "Ask for the next program year start date, then work the community fallback list below.",
  },
  {
    id: "reopening",
    term: "Reopening / next intake",
    meaning: "A date has been set for the next window.",
    action: "Put it in your calendar with a reminder a week ahead and have documents assembled before it arrives.",
  },
];

export interface ReopeningPattern {
  id: string;
  window: string;
  label: string;
  detail: string;
}

export const REOPENING_PATTERNS: ReopeningPattern[] = [
  {
    id: "federal-fy",
    window: "October",
    label: "Federal fiscal year begins",
    detail:
      "Many federally funded programs receive their new allocation at the start of the federal fiscal year in October, which is why heating assistance intake commonly opens in the fall.",
  },
  {
    id: "state-fy",
    window: "July",
    label: "State fiscal year begins",
    detail:
      "State-funded rental and housing programs frequently restart with the state budget year, which begins on July 1 in most states.",
  },
  {
    id: "cooling",
    window: "Late spring",
    label: "Cooling and summer crisis funds",
    detail:
      "Separate cooling or summer crisis components often open on their own schedule even when the heating component is closed.",
  },
  {
    id: "reallocation",
    window: "Any month",
    label: "Reallocation and unspent funds",
    detail:
      "Agencies sometimes reopen briefly when another county returns unspent money or a supplemental appropriation lands. These windows can be short and lightly publicized, which is why weekly rechecks matter.",
  },
];

export interface DocumentItem {
  id: string;
  label: string;
  detail: string;
}

export const DOCUMENT_CHECKLIST: DocumentItem[] = [
  { id: "id", label: "Photo ID for the applicant", detail: "Plus identification or birth records for everyone in the household, where requested." },
  { id: "income", label: "Income for the last 30 to 90 days", detail: "Pay stubs, benefit award letters, self-employment records, or a signed zero-income statement." },
  { id: "bill", label: "The current bill or notice", detail: "A shutoff notice, past-due utility bill, late rent notice, or eviction filing — most crisis funds require the notice itself." },
  { id: "lease", label: "Lease or mortgage statement", detail: "Showing your name, the address, and the monthly amount owed." },
  { id: "landlord", label: "Landlord or utility account details", detail: "Account number, payee name, W-9 or vendor form if the agency pays the provider directly." },
  { id: "residency", label: "Proof of residency", detail: "A recent bill or official mail at the address, since most programs are county-specific." },
  { id: "denial", label: "Any prior denial or approval letters", detail: "Useful when a second agency needs to see what has already been paid on your account." },
];

export interface QaBlock {
  id: string;
  q: string;
  a: string;
}

export const QUICK_ANSWERS: QaBlock[] = [
  {
    id: "utility-availability",
    q: "How do I find out if utility assistance funds are still available?",
    a: "Call 211 first for a same-week view of local funds, then call your utility's customer assistance team directly and ask about both grant money and hardship or arrearage programs. Utility-run funds follow their own calendar, so they can still have money when LIHEAP is closed. Confirm the answer with the local administrator, because county balances differ from statewide status.",
  },
  {
    id: "liheap-rental",
    q: "Is LIHEAP or rental assistance still open in my state?",
    a: "Check your state's LIHEAP page or housing finance agency status notice for a dated intake window, then confirm with the local Community Action Agency or county administrator that takes applications. Statewide pages often lag reality: a program can read as open while your county has already committed its allocation.",
  },
  {
    id: "why-run-out",
    q: "Why do assistance programs run out of funds?",
    a: "They are funded by a fixed annual block grant and paid out first-come, first-served. Seasonal demand arrives in a cluster, per-household benefit amounts have risen, and pandemic-era emergency supplements have largely expired. Together those factors commonly exhaust a year's allocation months before the program year ends.",
  },
];

export interface Faq {
  id: string;
  q: string;
  a: string;
}

export const FAQS: Faq[] = [
  {
    id: "when-reopen",
    q: "When do assistance programs usually reopen?",
    a: "Most reopen at the start of a new funding year — commonly October for federally funded programs and July for state-funded ones — but schedules vary by state and administrator. Short mid-year windows also happen when unspent funds are reallocated. Ask your local administrator for the specific date and recheck weekly if the program is listed as paused.",
  },
  {
    id: "official-status",
    q: "How do I check official program status rather than a third-party listing?",
    a: "Go to the agency that actually administers the money: your state energy or human services office for LIHEAP, your state housing finance agency for rental and housing programs, and the county or Community Action Agency that processes applications. Look for a dated notice. Third-party databases are useful for discovery but frequently lag on status.",
  },
  {
    id: "liheap-closed",
    q: "What should I do if LIHEAP is closed in my area?",
    a: "Ask the administrator for the next intake date and whether a waitlist exists, then contact your utility's hardship team about a payment arrangement, arrearage forgiveness, or shutoff protection. After that, work the community fallback list: nonprofits, faith networks, and utility-sponsored hardship funds often replenish on their own cycles.",
  },
  {
    id: "documents",
    q: "What documents do I need to apply?",
    a: "Photo ID, recent income documentation, the past-due bill or notice, your lease or mortgage statement, landlord or utility account details, and proof that you live in the service area. Assembling these before a program reopens is the single biggest factor in getting approved during a short window.",
  },
  {
    id: "waitlist-worth",
    q: "Is it worth joining a waitlist if funds are gone?",
    a: "Usually yes. Waitlist position often carries into the next funding round or into cancellations, and it puts your household in the administrator's system so you can be contacted the moment money is released.",
  },
  {
    id: "multiple-agencies",
    q: "Can I apply to more than one program at once?",
    a: "Generally yes, and it is commonly encouraged, though programs may coordinate so the same bill is not paid twice. Tell each agency what you have already applied for and share any approval or denial letters.",
  },
];

export const COMPLIANCE_NOTE =
  "Funding cycles, benefit amounts, intake windows, and reopening dates vary by state, county, utility, and administrator, and they change between funding rounds. The patterns described here are typical, not guarantees about any specific program. Always confirm current status directly with the agency that administers the money.";

export const DISCLAIMER =
  "Educational information only. Home Approach does not administer assistance programs, determine eligibility, or disburse funds. Contact your local administrator, 211, or a HUD-approved housing counselor for help with your specific situation.";

export const ASSISTANCE_FUNDING_JSON_LD: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      name: PAGE_TITLE,
      description: PAGE_DESCRIPTION,
      url: PAGE_URL,
      inLanguage: "en-US",
      isPartOf: { "@type": "WebSite", name: "Home Approach", url: SITE },
      publisher: {
        "@type": "Organization",
        name: "Home Approach",
        url: SITE,
        logo: { "@type": "ImageObject", url: `${SITE}/og-image.png` },
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "Tools", item: `${SITE}/tools` },
        { "@type": "ListItem", position: 3, name: PAGE_TITLE, item: PAGE_URL },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: [...QUICK_ANSWERS, ...FAQS].map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};
